import HTForm from '../../_shared/form/htform';
import { observable } from 'mobx';
import { rvalueRules, percentageRule, rValue2009StandardsWarning } from '../../_shared/validation/validationhelpers';

export default class FloorForm extends HTForm {
    currentRoomNum;

    @observable era = null;
    @observable zone = null;
    @observable retrofitted2009 = false;
    @observable standards2009Compliant = null;
    @observable rValueEra = null;

    constructor(props) {
        super(props);
        this.currentRoomNum = props.currentRoomNum;
    }

    setup() {
        return {
            fields: [
                // Add a hidden field to store the rvalue era, so we can use it in the showIf
                {
                    name: 'rvalueEra',
                    type: 'hidden',
                    value: this.rValueEra
                },
                {
                    name: 'floordetails',
                    type: 'formsection',
                    label: 'Floor details',
                    fields: [
                        {
                            name: 'floorintro',
                            type: 'content',
                            extra: {
                                description: () => {
                                    return this.getDescription([
                                        '<p>In this section we’ll ask you more questions about the floor, to understand how well it retains heat. Remember  to include any other space on this level that is always open to the main living room, like an open-plan kitchen, hallway or staircase.</p>'
                                    ]);
                                }
                            }
                        },
                        {
                            name: 'floorarea',
                            type: 'numberfield',
                            extra: {
                                unit: 'm2'
                            },
                            disabled: true,
                            value: null,
                            label: 'Your floor area according to section 2'
                        }
                    ]
                },
                {
                    name: 'floor',
                    type: 'formsection',
                    label: 'Floor insulation',
                    extra: {},
                    fields: [
                        // 4b
                        {
                            name: '4b',
                            type: 'radio',
                            value: 'external',
                            label: 'What type of space is below your floor?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'external',
                                            label:
                                                'External eg, a garage, piles, pillars, a concrete slab or other foundations'
                                        },
                                        {
                                            value: 'internal',
                                            label:
                                                "Internal eg, another room in your or someone else's home"
                                        },
                                        {
                                            value: 'mix',
                                            label:
                                                'A mix of external and internal'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand how much heat could be lost through the floor.</p>',
                                        `<p>${this.getHelpLink(13, 'Understanding if a floor is internal or external', 'help-floor_types')}</p>`
                                    ]);
                                }
                            }
                        },
                        // 4b-1
                        {
                            name: '4b-1',
                            type: 'radio',
                            value: 'all',
                            label:
                                'How much of your floor either has insulation that meets the required standard, or is a concrete floor on ground?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'all',
                                            label: 'All of the floor'
                                        },
                                        {
                                            value: 'none',
                                            label: 'None of the floor'
                                        },
                                        {
                                            value: 'some',
                                            label: 'Some of the floor'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This lets us use the R-value required under the healthy homes standards where you meet the standards.</p>',
                                        `<p>${this.getHealthyHomesLink()}</p>`
                                    ]);
                                },
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'external'] },
                                        {
                                            and: [
                                                { '!==': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '!==': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                },
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                }
                            }
                        },
                        // 4b-1a
                        {
                            name: '4b-1a',
                            type: 'rvaluefield',
                            label: 'Estimated R-value ',
                            value: null,
                            rules: rvalueRules.floor,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'floor',
                                surface: 'external',
                                standard: 'standard',
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                },
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'external'] },
                                        { '===': [{ var: '4b-1' }, 'all'] },
                                        {
                                            and: [
                                                { '!==': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '!==': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        // 4b-1b
                        {
                            name: '4b-1b',
                            type: 'rvaluefield',
                            label: 'Estimated R-value ',
                            value: 0,
                            rules: rvalueRules.floor,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'floor',
                                surface: 'external',
                                standard: 'nonstandard',
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'external'] },
                                        { '===': [{ var: '4b-1' }, 'none'] }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        // 4b-1c-1, 4b-1c-1p, 4b-1c-1r
                        {
                            name: '4b-1c-1',
                            type: 'percentrvaluefield',
                            label: null,
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'external'] },
                                        { '===': [{ var: '4b-1' }, 'some'] }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 4b-1c-1p
                                {
                                    name: '4b-1c-1p',
                                    type: 'numberfield',
                                    label:
                                        'What percentage of the floor has insulation that meets the standard?',
                                    value: null,
                                    rules: percentageRule,
                                    placeholder: 'Enter a percentage',
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    'floor.4b-1c-2.4b-1c-2p'
                                                );
                                            }
                                        }
                                    ]
                                },
                                // 4b-1c-1r
                                {
                                    name: '4b-1c-1r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value ',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'standard',
                                        parentWarning: (show = false) => {
                                            return this.retrofitted2009 && show
                                                ? rValue2009StandardsWarning
                                                : false;
                                        }
                                    }
                                }
                            ]
                        },
                        // 4b-1c-2 4b-1c-2p, 4b-1c-2r
                        {
                            name: '4b-1c-2',
                            type: 'percentrvaluefield',
                            label: null,
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'external'] },
                                        { '===': [{ var: '4b-1' }, 'some'] }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 4b-1c-2p
                                {
                                    name: '4b-1c-2p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the floor with no insulation, or insulation that does not meet the standard',
                                    value: null,
                                    disabled: true,
                                    extra: {
                                        unit: '%'
                                    }
                                },
                                // 4b-1c-2r
                                {
                                    name: '4b-1c-2r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'nonstandard'
                                    }
                                }
                            ]
                        },
                        // 4b-2
                        {
                            name: '4b-2',
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: null,
                            rules: rvalueRules.floor,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'floor',
                                surface: 'internal',
                                showIf: {
                                    '===': [{ var: '4b' }, 'internal']
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. Most homes do not have insulation in internal floors so the tool assumes this. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        // 4b-3c (content)
                        {
                            name: '4b-3c',
                            type: 'content',
                            label:
                                'How much of the floor is internal and how much is external?',
                            extra: {
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand how much heat could be lost through the floor.</p>',
                                        `<p>${this.getHelpLink(14, 'Finding the percentage of internal and external floor', 'help-mixed_floors')}</p>`
                                    ]);
                                },
                                showIf: {
                                    '===': [{ var: '4b' }, 'mix']
                                }
                            }
                        },
                        // 4b-3: 4b-3p, 4b-3r
                        {
                            name: '4b-3',
                            type: 'percentrvaluefield',
                            label: 'What percentage of the floor is internal?',
                            extra: {
                                showIf: {
                                    '===': [{ var: '4b' }, 'mix']
                                },
                                hint: 'Most houses do not have insulation in internal floors so the tool assumes this. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 4b-3p
                                {
                                    name: '4b-3p',
                                    type: 'numberfield',
                                    label:
                                        'What percentage of the floor is internal?',
                                    value: null,
                                    rules: percentageRule,
                                    placeholder: 'Enter a percentage',
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    [
                                                        'floor.4b-3a-1.4b-3a-1p',
                                                        'floor.4b-3a-2.4b-3a-2p'
                                                    ]
                                                );
                                            }
                                        }
                                    ]
                                },
                                // 4b-3r
                                {
                                    name: '4b-3r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: 'required|numeric|min:0|max:32',
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'internal',
                                        standard: 'nonstandard'
                                    }
                                }
                            ]
                        },
                        // 4b-3a
                        {
                            name: '4b-3a',
                            type: 'radio',
                            value: 'all',
                            label:
                                'How much of the external floor has insulation that meets the required standard?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'all',
                                            label: 'All of the external floor '
                                        },
                                        {
                                            value: 'none',
                                            label: 'None of the external floor'
                                        },
                                        {
                                            value: 'some',
                                            label: 'Some of the external floor'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This lets us use the R-value required under the healthy homes standards where you meet the standards.</p>',
                                        `<p>${this.getHealthyHomesLink()}</p>`
                                    ]);
                                },
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'mix'] },
                                        {
                                            and: [
                                                { '!==': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '!==': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                },
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                }
                            }
                        },
                        // 4b-3a-1: 4b-3a-1p, 4b-3a-1r
                        {
                            name: '4b-3a-1',
                            type: 'percentrvaluefield',
                            label: 'Percentage of the floor that is external',
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'mix'] },
                                        { '===': [{ var: '4b-3a' }, 'all'] },
                                        {
                                            and: [
                                                { '!==': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '!==': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                },
                                hint: 'You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 4b-3a-1p
                                {
                                    name: '4b-3a-1p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the floor that is external',
                                    value: null,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                // 4b-3a-1r
                                {
                                    name: '4b-3a-1r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'standard',
                                        parentWarning: (show = false) => {
                                            return this.retrofitted2009 && show
                                                ? rValue2009StandardsWarning
                                                : false;
                                        }
                                    }
                                }
                            ]
                        },
                        // 4b-3a-2: 4b-3a-2p, 4b-3a-2r
                        {
                            name: '4b-3a-2',
                            type: 'percentrvaluefield',
                            label: null,
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'mix'] },
                                        { '===': [{ var: '4b-3a' }, 'none'] }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 4b-3a-2p
                                {
                                    name: '4b-3a-2p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of floor that has external space below it?',
                                    value: null,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                // 4b-3a-2r
                                {
                                    name: '4b-3a-2r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'nonstandard'
                                    }
                                }
                            ]
                        },
                        // 4b-3a-3-1: 4b-3a-3-1p, 4b-3a-3-1r
                        {
                            name: '4b-3a-3-1',
                            type: 'percentrvaluefield',
                            label: 'What percentage of the external floor has insulation that meets the standard?',
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'mix'] },
                                        { '===': [{ var: '4b-3a' }, 'some'] }
                                    ]
                                }
                            },
                            fields: [
                                // 4b-3a-3-1p
                                {
                                    name: '4b-3a-3-1p',
                                    type: 'numberfield',
                                    label:
                                        'What percentage of the external floor has insulation that meets the standard?',
                                    value: null,
                                    rules: percentageRule,
                                    placeholder: 'Enter a percentage',
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    [
                                                        'floor.4b-3a-3-2.4b-3a-3-2p'
                                                    ]
                                                );
                                            }
                                        }
                                    ]
                                },
                                // 4b-3a-3-1r
                                {
                                    name: '4b-3a-3-1r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'standard',
                                        parentWarning: (show = false) => {
                                            return this.retrofitted2009 && show
                                                ? rValue2009StandardsWarning
                                                : false;
                                        }
                                    }
                                }
                            ]
                        },
                        // 4b-3a-3-2: 4b-3a-3-2p, 4b-3a-3-2r
                        {
                            name: '4b-3a-3-2',
                            type: 'percentrvaluefield',
                            label: 'Percentage of the external floor with no insulation, or insulation that does not meet the standard',
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '4b' }, 'mix'] },
                                        { '===': [{ var: '4b-3a' }, 'some'] }
                                    ]
                                },
                                hint: 'These estimates include the R-value of your floor insulation (where applicable). These R-values are only an estimate — you can edit them if you know the exact value. If you know an R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                {
                                    name: '4b-3a-3-2p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the external floor with no insulation, or insulation that does not meet the standard',
                                    value: null,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                {
                                    name: '4b-3a-3-2r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'nonstandard'
                                    }
                                }
                            ]
                        },
                        // 4b-4
                        {
                            name: '4b-4',
                            type: 'radio',
                            value: 'slab',
                            label:
                                'What is the construction type of the external floor?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'slab',
                                            label: 'Slab-on-ground'
                                        },
                                        {
                                            value: 'suspended',
                                            label: 'Suspended floor eg, over a garage, piles, or pillars'
                                        },
                                        {
                                            value: 'mix',
                                            label: 'A mix of slab-on-ground and suspended floor'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>Based on the construction type of the external floor, we can estimate how well the floor retains heat (the R-value).</p>',
                                        `<p>${this.getHealthyHomesLink()}</p>`
                                    ]);
                                },
                                showIf: {
                                    and: [
                                        {
                                            or: [
                                                { '===': [{ var: '4b' }, 'external'] },
                                                { '===': [{ var: '4b' }, 'mix'] }
                                            ]
                                        },
                                        {
                                            or: [
                                                { '===': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '===': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                }
                            }
                        },
                        // 4b-4-1a
                        {
                            name: '4b-4-1a',
                            type: 'percentrvaluefield',
                            label: 'What percentage of the external floor is slab-on-ground?',
                            extra: {
                                showIf: {
                                    and: [
                                        {
                                            or: [
                                                { '===': [{ var: '4b' }, 'external'] },
                                                { '===': [{ var: '4b' }, 'mix'] }
                                            ]
                                        },
                                        { '===': [{ var: '4b-4' }, 'mix'] },
                                        {
                                            or: [
                                                { '===': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '===': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                },
                                hint: 'This estimated R-value is for the overall slab-on-ground floor, including any floor insulation. These R-values are only an estimate — you can edit them if you know the exact value. If you know an R-value that is lower, you must enter it. Even where there is no insulation, a wall, ceiling, or floor will still have an R-value.'
                            },
                            fields: [
                                // 4b-4-1a-p
                                {
                                    name: '4b-4-1a-p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the external floor that is slab on ground',
                                    value: null,
                                    rules: percentageRule,
                                    placeholder: 'Enter a percentage',
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    [
                                                        'floor.4b-4-1b.4b-4-1b-p'
                                                    ]
                                                );
                                            }
                                        }
                                    ]
                                },
                                // 4b-4-1a-r
                                {
                                    name: '4b-4-1a-r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'slab',
                                        standard: 'standard'
                                    }
                                }
                            ]
                        },
                        // 4b-4-1b
                        {
                            name: '4b-4-1b',
                            type: 'percentrvaluefield',
                            label: 'What percentage of of the external floor is suspended?',
                            extra: {
                                showIf: {
                                    and: [
                                        {
                                            or: [
                                                { '===': [{ var: '4b' }, 'external'] },
                                                { '===': [{ var: '4b' }, 'mix'] }
                                            ]
                                        },
                                        { '===': [{ var: '4b-4' }, 'mix'] }
                                    ]
                                },
                                hint: 'This estimated R-value is for the overall suspended floor, including any floor insulation. These R-values are only an estimate — you can edit them if you know the exact value. If you know an R-value that is lower, you must enter it. Even where there is no insulation, a wall, ceiling, or floor will still have an R-value.'
                            },
                            fields: [
                                {
                                    name: '4b-4-1b-p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of floor that is suspended',
                                    value: null,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                {
                                    name: '4b-4-1b-r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'standard'
                                    }
                                }
                            ]
                        },
                        {
                            name: '4b-4-1c',
                            type: 'rvaluefield',
                            label: 'Estimated R-value ',
                            value: null,
                            rules: rvalueRules.floor,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'floor',
                                surface: 'slab',
                                standard: 'standard',
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                },
                                showIf: {
                                    and: [
                                        {
                                            or: [
                                                { '===': [{ var: '4b' }, 'external'] },
                                                { '===': [{ var: '4b' }, 'mix'] }
                                            ]
                                        },
                                        { '===': [{ var: '4b-4' }, 'slab'] },
                                        {
                                            or: [
                                                { '===': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '===': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        {
                            name: '4b-4-1d',
                            type: 'rvaluefield',
                            label: 'Estimated R-value ',
                            value: null,
                            rules: rvalueRules.floor,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'floor',
                                surface: 'external',
                                standard: 'standard',
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                },
                                showIf: {
                                    and: [
                                        {
                                            or: [
                                                { '===': [{ var: '4b' }, 'external'] },
                                                { '===': [{ var: '4b' }, 'mix'] }
                                            ]
                                        },
                                        { '===': [{ var: '4b-4' }, 'suspended'] },
                                        {
                                            or: [
                                                { '===': [{ var: 'rvalueEra' }, 'From 1 May 2023 to 1 Nov 2023'] },
                                                { '===': [{ var: 'rvalueEra' }, 'On or after 2 Nov 2023'] }
                                            ]
                                        }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your floor insulation. You can edit this estimate if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        }
                    ]
                }
            ]
        };
    }
}
